import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Card, Form, Row, Col, Spinner, Badge } from "react-bootstrap";
import Select from 'react-select';
import Swal from "sweetalert2";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";

const SupplierView = () => {
  const { id } = useParams();
  const { pointindex } = useParams();
  const navigate = useNavigate();
  const udyamTypeOptions = [
    { value: 'Micro', label: 'Micro' },
    { value: 'Small', label: 'Small' },
    { value: 'Medium', label: 'Medium' }
  ];
  const [supplierData, setSupplierData] = useState({
    companyname: "",
    contactperson: "",
    contactno: "",
    gstno: "",
    udyamnumber: "",
    udyamtype: null,
    email: "",
    packingandforwarding: "",
    paymentterm: '',
    freight: "",
    incoterms: "",
    accountno: "",
    bankname: "",
    bankbranchname: "",
    ifsccode: ""
  });

  const [addresses, setAddresses] = useState([{
    supplieraddress: "",
    country: null,
    state: null,
    city: null,
    stateOptions: [],
    cityOptions: [],
    statecode: "",
    pincode: "",
  }]);

  const [countryOptions, setCountryOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${BASE_URL}country/findAllCountry`, {
          headers: authHeader(),
        });
        setCountryOptions(response.data.map(country => ({ value: country.id, label: country.countryname })));
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch countries. Please try again.',
        });
      }
    };

    const fetchSupplier = async () => {
      if (id) {
        try {
          const response = await axios.get(`${BASE_URL}supplier/findById/${id}`, {
            headers: authHeader(),
          });
          const data = response.data;

          setSupplierData({
            companyname: data.companyname,
            contactperson: data.contactperson,
            contactno: data.contactno,
            gstno: data.gstno,
            udyamnumber: data.udyamnumber || "",
            udyamtype: data.udyamtype
              ? udyamTypeOptions.find(opt => opt.value === data.udyamtype)
              : null,
            email: data.email,
            packingandforwarding: data.packingandforwarding,
            paymentterm: data.paymentterm,
            freight: data.freight,
            incoterms: data.incoterms,
            accountno: data.accountno,
            bankname: data.bankname,
            bankbranchname: data.bankbranchname,
            ifsccode: data.ifsccode
          });

          const supplieraddresses = data.supplierMultipleAddress.map(addr => ({
            supplieraddress: addr.supplieraddress,
            country: addr.country ? { value: addr.country.id, label: addr.country.countryname } : null,
            state: addr.state ? { value: addr.state.id, label: addr.state.statename, code: addr.state.statecode } : null,
            city: addr.city ? { value: addr.city.id, label: addr.city.cityname } : null,
            stateOptions: [],
            cityOptions: [],
            statecode: addr.statecode,
            pincode: addr.pincode,
          }));

          setAddresses(supplieraddresses);
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to fetch supplier details. Please try again.',
          });
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCountries();
    fetchSupplier();
  }, [id]);

  const getItem = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(BASE_URL + "supplier/delete/" + id, {
          headers: authHeader(),
        })
          .then(response => {
            if (response.data.length === 0) {
              Swal.fire(
                'Warning',
                'Supplier present in another module. Delete operation has been cancelled.',
                'warning'
              );
            } else {
              navigate(`/supplierindex/${pointindex}`);
              Swal.fire(
                'Deleted!',
                'Supplier deleted successfully.',
                'success'
              );
            }
          })
          .catch((error) => {
            Swal.fire(
              'Error',
              error.response.data,
              'error'
            );
          });
      }
    });
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12">
          <Card className="shadow-sm">
            <Card.Header className=" d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-truck mr-2"></i>
                Supplier Details
              </h5>
              <div>
                <Link to={`/contactSTable/${id}/${pointindex}`} className="button-70 btn-light mr-2">
                  <i className="fas fa-user-plus mr-1"></i> Add Contact
                </Link>
                <button onClick={() => getItem(id)} className="button-70 btn-danger mr-2">
                  <i className="fas fa-trash mr-1"></i> Delete
                </button>
                <Link to={`/supplierEdit/${id}/${pointindex}`} className="button-70 btn-warning mr-2">
                  <i className="fas fa-edit mr-1"></i> Edit
                </Link>
                <Link to={`/supplierindex/${pointindex}`} className="button-70 btn-light">
                  <i className="fas fa-arrow-left mr-1"></i> Back
                </Link>
              </div>
            </Card.Header>

            <Card.Body>
              <div className="mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fas fa-building mr-2"></i>Basic Information
                </h6>
                <Row>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Company Name</Form.Label>
                      <Form.Control plaintext readOnly value={supplierData.companyname || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Contact Person</Form.Label>
                      <Form.Control plaintext readOnly value={supplierData.contactperson || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Mobile No.</Form.Label>
                      <Form.Control plaintext readOnly value={supplierData.contactno || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Email</Form.Label>
                      <Form.Control plaintext readOnly value={supplierData.email || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">GST No.</Form.Label>
                      <Form.Control plaintext readOnly value={supplierData.gstno || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md="4">
                    <Form.Group controlId={`formudyamnumber`}>
                      <Form.Label>Udyam Number:</Form.Label>
                      <Form.Control
                        type="text"
                        value={supplierData.udyamnumber || ""}
                        readOnly
                        plaintext
                      />
                    </Form.Group>
                  </Col>
                  <Col md="4">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Udyam Type</Form.Label>
                      <Form.Control
                        plaintext
                        readOnly
                        value={supplierData.udyamtype?.label || "N/A"}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <div className="mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fas fa-percentage mr-2"></i>Pricing Terms
                </h6>
                <Row>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Packing & Forwarding (%)</Form.Label>
                      <Form.Control plaintext readOnly value={supplierData.packingandforwarding || "0"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Freight (%)</Form.Label>
                      <Form.Control plaintext readOnly value={supplierData.freight || "0"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Payment Terms</Form.Label>
                      <Form.Control plaintext readOnly value={supplierData.paymentterm || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Inco Terms</Form.Label>
                      <Form.Control plaintext readOnly value={supplierData.incoterms || "N/A"} />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <div className="mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fas fa-university mr-2"></i>Bank Details
                </h6>
                <Row>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Bank Name</Form.Label>
                      <Form.Control plaintext readOnly value={supplierData.bankname || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Account No.</Form.Label>
                      <Form.Control plaintext readOnly value={supplierData.accountno || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Branch Name</Form.Label>
                      <Form.Control plaintext readOnly value={supplierData.bankbranchname || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">IFSC Code</Form.Label>
                      <Form.Control plaintext readOnly value={supplierData.ifsccode || "N/A"} />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <div className="mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fas fa-map-marker-alt mr-2"></i>Address Details
                </h6>
                {addresses.map((addr, index) => (
                  <Card key={index} className="mb-3 border">
                    <Card.Header className="bg-light d-flex justify-content-between">
                      <span className="font-weight-bold">Address #{index + 1}</span>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={12} className="mb-3">
                          <Form.Group>
                            <Form.Label className="font-weight-bold">Address Line</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              plaintext
                              readOnly
                              value={addr.supplieraddress || "N/A"}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Form.Group>
                            <Form.Label className="font-weight-bold">Country</Form.Label>
                            <Select
                              value={addr.country}
                              options={countryOptions}
                              isDisabled
                              placeholder="Not specified"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Form.Group>
                            <Form.Label className="font-weight-bold">State</Form.Label>
                            <Select
                              value={addr.state}
                              options={addr.stateOptions}
                              isDisabled
                              placeholder="Not specified"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Form.Group>
                            <Form.Label className="font-weight-bold">State Code</Form.Label>
                            <Form.Control plaintext readOnly value={addr.statecode || "N/A"} />
                          </Form.Group>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Form.Group>
                            <Form.Label className="font-weight-bold">City</Form.Label>
                            <Select
                              value={addr.city}
                              options={addr.cityOptions}
                              isDisabled
                              placeholder="Not specified"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Form.Group>
                            <Form.Label className="font-weight-bold">Pincode</Form.Label>
                            <Form.Control plaintext readOnly value={addr.pincode || "N/A"} />
                          </Form.Group>
                        </Col>
                        <Col md="4"></Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </Card.Body>

            <Card.Footer className="bg-light">
              <div className="d-flex justify-content-end">
                <Link to={`/supplierindex/${pointindex}`} className="btn btn-secondary mr-2">
                  <i className="fas fa-arrow-left mr-1"></i> Back to List
                </Link>
                <Link to={`/supplierEdit/${id}/${pointindex}`} className="btn btn-primary">
                  <i className="fas fa-edit mr-1"></i> Edit Supplier
                </Link>
              </div>
            </Card.Footer>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SupplierView;
